// Rota Drafts list page - shows all rota draft periods grouped by week.
//
// Manager workflow:
//   - Open this page from Drafts > Rota tab (or Settings > Draft next week's
//     rota > View latest draft)
//   - See a row per week with status, summary copy and quick actions
//   - Click into a row to review the full week grid

import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Loader2, RefreshCw, ChevronRight, CalendarDays, Sparkles,
} from "lucide-react";

const STATUS_LABEL = {
  awaiting_review: { label: "Awaiting review", tone: "amber"   },
  approved:        { label: "Approved",        tone: "blue"    },
  published:       { label: "Published",       tone: "emerald" },
  discarded:       { label: "Discarded",       tone: "slate"   },
  superseded:      { label: "Superseded",      tone: "slate"   },
  generating:      { label: "Generating",      tone: "blue"    },
};

const TONE_CLASSES = {
  amber:   "bg-amber-50 text-amber-900 border-amber-200",
  emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
  slate:   "bg-slate-50 text-slate-600 border-slate-200",
  blue:    "bg-blue-50 text-blue-800 border-blue-200",
};

function formatRange(start, end) {
  if (!start || !end) return "";
  try {
    const a = new Date(start + "T00:00:00Z");
    const b = new Date(end + "T00:00:00Z");
    const fmt = (d) => d.toLocaleDateString("en-GB",
        { day: "numeric", month: "short" });
    return `${fmt(a)} - ${fmt(b)} ${b.getUTCFullYear()}`;
  } catch (e) {
    return `${start} to ${end}`;
  }
}

export default function DraftsRotaListPage() {
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/drafts/rota");
      setPeriods(data.periods || []);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Could not load drafts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const generate = useCallback(async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/drafts/rota/run-now", {
        horizon_weeks: 1,
        confidence_floor: "strong_only",
      });
      if (data.created?.length) {
        toast.success(`Created ${data.created.length} draft rota period(s).`);
      } else if (data.skipped?.length) {
        const first = data.skipped[0];
        toast.message(first.message || "No new periods generated.");
      } else {
        toast.message("Nothing to draft.");
      }
      await load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Generation failed");
    } finally {
      setGenerating(false);
    }
  }, [load]);

  return (
    <div data-testid="drafts-rota-list-page">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#0B1A36] flex items-center gap-2">
            <CalendarDays className="h-6 w-6 text-[#2563EB]" />
            Draft rotas
          </h1>
          <p className="text-sm text-[#4B5773] mt-1 max-w-2xl">
            Review weeks NextRota has drafted for you. Approve a week to push
            every shift to the live roster in one go, or discard the draft
            and start again.
          </p>
        </div>
        <Button onClick={generate} disabled={generating}
                data-testid="rota-generate-now">
          {generating ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Sparkles className="h-4 w-4 mr-2" />
          )}
          Generate next week
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-5 w-5 animate-spin text-[#4B5773]" />
        </div>
      ) : periods.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50/60 p-8 text-center"
             data-testid="rota-empty-state">
          <CalendarDays className="h-8 w-8 text-slate-400 mx-auto mb-2" />
          <h3 className="font-medium text-[#0B1A36]">No draft rotas yet</h3>
          <p className="text-sm text-[#4B5773] mt-1 max-w-md mx-auto">
            Click <strong>Generate next week</strong> to create your first
            draft. NextRota will pick the strongest available person for
            every uncovered shift in the week ahead.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {periods.map((p) => {
            const sl = STATUS_LABEL[p.status] || { label: p.status, tone: "slate" };
            return (
              <Link key={p.period_id}
                    to={`/app/drafts/rota/${p.period_id}`}
                    className="block rounded-lg border bg-white hover:bg-slate-50 transition p-4"
                    data-testid={`rota-period-${p.period_id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-medium text-[#0B1A36]">
                        Week of {formatRange(p.window_start, p.window_end)}
                      </h3>
                      <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${TONE_CLASSES[sl.tone]}`}
                            data-testid={`rota-status-${p.period_id}`}>
                        {sl.label}
                      </span>
                    </div>
                    <p className="text-sm text-[#4B5773] mt-1">
                      {p.manager_copy}
                    </p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-[#4B5773] mt-1 shrink-0" />
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
